<script lang="ts">
import { DataRow } from '@/table/commonTypes';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import ElTableMobile, { ElMobileTableProps, MobileTableFilter, MobileTableRow } from '@/table/ElMobileTable.vue';
import ElTable from './ElTable.vue';
import { ElTextCellColor, ElTextCellStyle } from '@/ElTextCell.vue';
import { ElIconProps } from '@/ElIcon.vue';
import ElButton from '@/ElButton.vue';
import { ActionCell, DefaultCell, TagCell } from './ElTableCell.vue';
import {
  ElTagColor,
  TableColumnFilterDateRange,
  TableColumnFilterFreeSearch,
  TableColumnFilterMultiValue,
  TableColumnFilterResetButton,
} from '..';

export type ResponsiveDataCell = ResponsiveDefaultDataCell | ResponsiveTagDataCell | ResponsiveActionDataCell;

export type ResponsiveDefaultDataCell = Omit<DefaultCell, 'mainIcon' | 'subIcon' | 'longText' | 'truncateSubText'> & {
  desktopBehaviour?: Pick<DefaultCell, 'mainIcon' | 'subIcon' | 'longText' | 'truncateSubText'>;
  mobileBehaviour?: {
    tertiaryText?: string;
    amountText?: string;
    timeText?: string;
  };
};

export type ResponsiveTagDataCell = Omit<TagCell, 'subText' | 'truncateSubText' | 'tag'> & {
  type: 'tag';
  // @deprecated use desktopBehaviour.tag.text instead
  text?: string;
  // @deprecated use desktopBehaviour.tag.color instead
  color?: ElTagColor;
  desktopBehaviour?: Pick<TagCell, 'subText' | 'truncateSubText' | 'tag'>;
};

export type ResponsiveActionDataCell = ActionCell & {
  type: 'action';
  mobileBehaviour?: {
    // index of the button to use as primary action
    primary: InstanceType<typeof ElButton>['$props'];
    // index of the button to use as secondary action
    secondary?: InstanceType<typeof ElButton>['$props'];
  };
};

export type ResponsiveDataRow<T = any> = {
  id: string;
  rowNotSelectable?: boolean;
  relatedObject?: T;
  cells: ResponsiveDataCell[];
  mobileBehaviour?: {
    trailingIcon?: ElIconProps;
  };
};

// If filter of type 'MULTI_VALUE' is used and mobileBehaviour.position is a list
// Then the first will be used for the first mapping unless specified otherwise
// In filter.mobileBehaviour.position
export type ResponsiveTableColumn = {
  title: string;
  filter?: ResponiveTableColumnFilter;
  desktopBehaviour?: {
    alignRight?: boolean;
    noSort?: boolean;
  };
  mobileBehaviour?: {
    position:
      | 'mainText'
      | 'secondaryRow'
      | 'tertiaryRow'
      | 'amount'
      | 'time'
      | 'tags'
      | 'avatar'
      | (
          | {
              take: keyof Pick<ResponsiveDefaultDataCell, 'mainText' | 'subText'>;
              to: 'mainText' | 'secondaryRow' | 'tertiaryRow' | 'amount' | 'time';
              withLabel: string;
            }
          | {
              withLabel: string;
              to: 'avatar';
            }
        )[];
  };
};

export type ResponiveTableColumnFilter =
  | TableColumnFilterFreeSearch
  | TableColumnFilterDateRange
  | (TableColumnFilterMultiValue & {
      mobileBehaviour?: {
        /**
         * Used only if filter type === 'MULTI_VALUE' and column.mobileBehaviour.position is an array
         */
        position?: 'mainText' | 'secondaryRow' | 'tertiaryRow' | 'amount' | 'time' | 'tags' | 'avatar';
      };
    })
  | TableColumnFilterResetButton;

export interface ElResponsiveTableProps {
  columns: ResponsiveTableColumn[];
  data: ResponsiveDataRow[];
  tableId?: number | string; // needed to understand when the table changes cols and data
  noFilters?: boolean;
  rowsSelectionMode?: 'single' | 'multiple';
  rowsSelectionDisabled?: boolean;
  color: 'primary' | 'secondary';
  loading?: boolean;
  sortBy?: {
    column?: number;
    order?: 'asc' | 'desc';
  };
  desktopBehaviour?: {
    noFooter?: boolean;
    initialRows?: number;
  };
  mobileBehaviour?: Pick<ElMobileTableProps, 'preventDefaultItemClick'> & {
    selection?: Pick<
      NonNullable<ElMobileTableProps['selection']>,
      'destructiveAction' | 'bulkActionButton' | 'selectedLabel' | 'cancelText'
    >;
  };
}
</script>

<script lang="ts" setup>
const mediaQuery = window.matchMedia('(max-width: 768px)');
const isMobile = ref(mediaQuery.matches);
const props = defineProps<ElResponsiveTableProps>();

const onMatchMediaChange = (event: MediaQueryListEvent) => {
  isMobile.value = event.matches;
};
onMounted(() => {
  mediaQuery.addEventListener('change', onMatchMediaChange);
});
onUnmounted(() => {
  mediaQuery.removeEventListener('change', onMatchMediaChange);
});

const emit = defineEmits<{
  (event: 'rows-selected', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:bulk:click', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:destructive:click', rows: ResponsiveDataRow[]): void;
  (event: 'mobile:selection:exit'): void;
  (event: 'mobile:item:click', row: ResponsiveDataRow): void;
}>();
const desktopTable = ref<InstanceType<typeof ElTable> | null>(null);
const mobileTable = ref<InstanceType<typeof ElTableMobile> | null>(null);

const rowsById = computed(() =>
  props.data.reduce(
    (acc, row) => {
      acc[row.id] = row;
      return acc;
    },
    {} as Record<string, ResponsiveDataRow>,
  ),
);

const filteredData = computed(() => {
  return isMobile.value ? mobileTable.value?.filteredData : desktopTable.value?.filteredData;
});

const selectRows = async (rowsIds: string[]) => {
  if (isMobile.value) {
    mobileTable.value?.selectRows(rowsIds);
  } else {
    desktopTable.value?.selectRows(rowsIds);
  }
};

const unselectAllRows = () => {
  if (isMobile.value) {
    mobileTable.value?.unselectAllRows();
  } else {
    desktopTable.value?.unselectAllRows();
  }
};

const responsiveRowToDesktopRow = (item: ResponsiveDataRow): DataRow => {
  return {
    id: item.id,
    rowNotSelectable: item.rowNotSelectable,
    relatedObject: item.relatedObject,
    cells: item.cells.map(cell => {
      if (!cell.type) return { type: 'default', ...cell };

      switch (cell.type) {
        case 'default':
          return {
            type: 'default',
            mainIcon: cell.desktopBehaviour?.mainIcon,
            mainText: cell.mainText,
            mainTextStyle: cell.mainTextStyle,
            mainTextColor: cell.mainTextColor,
            subIcon: cell.desktopBehaviour?.subIcon,
            subText: cell.subText,
            avatar: cell.avatar,
            clickAction: cell.clickAction,
            longText: cell.desktopBehaviour?.longText,
            truncateSubText: cell.desktopBehaviour?.truncateSubText,
          } satisfies DefaultCell;
        case 'tag':
          return {
            type: 'tag',
            tag: { text: cell.text ?? '', color: cell.color, ...cell.desktopBehaviour?.tag },
            subText: cell.desktopBehaviour?.subText,
            truncateSubText: cell.desktopBehaviour?.truncateSubText,
          } satisfies TagCell;
        case 'action':
          return {
            type: 'action',
            buttons: cell.buttons,
            iconButtons: cell.iconButtons,
            dropdown: cell.dropdown,
          } satisfies ActionCell;
      }
    }),
  } satisfies DataRow;
};

const getDataRows = () => {
  if (!isMobile.value) return desktopTable.value?.getDataRows().map(row => rowsById.value[row.id]) || [];
  return mobileTable.value?.getDataRows().map(row => rowsById.value[row.id]) || [];
};

const getSelectedRows = () => {
  if (!isMobile.value) return desktopTable.value?.getSelectedRows().map(row => rowsById.value[row.id]) || [];
  return mobileTable.value?.getSelectedRows().map(row => rowsById.value[row.id]) || [];
};

const closeMobileTableItemDetails = () => {
  mobileTable.value?.closeItemDetails();
};

defineExpose({
  filteredData,
  selectRows,
  unselectAllRows,
  getDataRows,
  getSelectedRows,
  closeMobileTableItemDetails,
});

const mobileItems = computed<MobileTableRow[]>(() => {
  return props.data.map(item => {
    let mainText: string | undefined;
    let mainTextStyle: ElTextCellStyle | undefined;
    let mainTextColor: ElTextCellColor | undefined;
    let secondaryText: string | undefined;
    let tertiaryText: string | undefined;
    let amountText: string | undefined;
    let avatarLabel: string | undefined;
    let avatarPicture: string | undefined;
    let timeText: string | undefined;
    const tags: MobileTableRow['tags'] = [];
    let primaryAction: InstanceType<typeof ElButton>['$props'] | undefined;
    let secondaryAction: InstanceType<typeof ElButton>['$props'] | undefined;
    const details: MobileTableRow['details'] = [];

    for (const [cellIndex, cell] of item.cells.entries()) {
      const column = props.columns[cellIndex];
      if (!column) throw new Error('Missing column for cell at index ' + cellIndex);

      if (cell.type === 'default') {
        if (Array.isArray(column.mobileBehaviour?.position)) {
          for (const mapping of column.mobileBehaviour.position) {
            if (mapping.to === 'avatar') {
              details.push({
                label: mapping.withLabel,
                value: { type: 'avatar', ...cell.avatar },
              });
            } else {
              details.push({
                label: mapping.withLabel,
                value: { type: 'default', text: cell[mapping.take] ?? '' },
              });
            }
          }
        } else {
          if (cell.avatar) {
            details.push({
              label: column.title,
              value: { type: 'avatar', ...cell.avatar },
            });
          } else {
            details.push({
              label: column.title,
              value: { type: 'default', text: cell.mainText },
            });
          }
        }
      } else if (cell.type === 'tag') {
        details.push({
          label: column.title,
          value: {
            type: 'tag',
            text: cell.desktopBehaviour?.tag.text ?? cell.text ?? '',
            color: cell.desktopBehaviour?.tag.color ?? cell.color,
          },
        });
      }

      const mobileColumn = column.mobileBehaviour;
      if (!mobileColumn && cell.type !== 'action') continue;
      if (cell.type === 'default') {
        if (Array.isArray(mobileColumn!.position)) {
          for (const mapping of mobileColumn!.position) {
            switch (mapping.to) {
              case 'time':
                timeText = cell[mapping.take];
                break;
              case 'mainText':
                mainText = cell[mapping.take];
                mainTextStyle = cell.mainTextStyle;
                mainTextColor = cell.mainTextColor;
                break;
              case 'secondaryRow':
                secondaryText = cell[mapping.take];
                break;
              case 'tertiaryRow':
                tertiaryText = cell[mapping.take];
                break;
              case 'amount':
                amountText = cell[mapping.take];
                break;
              case 'avatar':
                avatarLabel = cell.avatar?.label;
                avatarPicture = cell.avatar?.picture;
                break;
            }
          }
        } else {
          switch (mobileColumn!.position) {
            case 'mainText':
              mainText = cell.mainText;
              mainTextStyle = cell.mainTextStyle;
              mainTextColor = cell.mainTextColor;
              break;
            case 'secondaryRow':
              secondaryText = cell.subText ?? cell.mainText;
              break;
            case 'avatar':
              avatarLabel = cell.avatar?.label;
              avatarPicture = cell.avatar?.picture;
              break;
            case 'amount':
              amountText = cell.mobileBehaviour?.amountText ?? cell.mainText;
              break;
            case 'tertiaryRow':
              tertiaryText = cell.mobileBehaviour?.tertiaryText ?? cell.mainText;
              break;
            case 'tags':
              console.warn('Skipping tags mobile position on cell type default, for tags to be used in mobile table, use cell type tag');
              break;
            case 'time':
              timeText = cell.mobileBehaviour?.timeText ?? cell.mainText;
              break;
          }
          continue;
        }
      }

      if (cell.type === 'tag' && mobileColumn!.position === 'tags') {
        const text = cell.desktopBehaviour?.tag.text ?? cell.text ?? '';
        tags.push({
          key: text,
          text: text,
          color: cell.desktopBehaviour?.tag.color ?? cell.color,
        });
        continue;
      }

      if (cell.type == 'action') {
        if (cell.mobileBehaviour?.primary) {
          primaryAction = cell.mobileBehaviour.primary;
        }
        if (cell.mobileBehaviour?.secondary) {
          secondaryAction = cell.mobileBehaviour.secondary;
        }
      }
    }

    let actions: MobileTableRow['actions'];
    if (primaryAction) {
      actions = {
        primary: primaryAction,
        secondary: secondaryAction,
      };
    }

    if (!mainText) throw new Error('Missing mainText for mobile item');

    return {
      id: item.id,
      rowNotSelectable: item.rowNotSelectable,
      relatedObject: item.relatedObject,
      time: timeText,
      mainText: {
        label: mainText,
        style: mainTextStyle,
        color: mainTextColor,
      },
      secondaryRow: secondaryText,
      tertiaryRow: tertiaryText,
      tags,
      amount: amountText,
      avatar: avatarLabel || avatarPicture ? ({ label: avatarLabel, picture: avatarPicture } as MobileTableRow['avatar']) : undefined,
      trailingIcon: item.mobileBehaviour?.trailingIcon,
      actions,
      details,
    } satisfies MobileTableRow;
  });
});

const desktopItems = computed<DataRow[]>(() => {
  return props.data.map(responsiveRowToDesktopRow);
});

const mobileFilters = computed<MobileTableFilter>(() => {
  const mobileFilter: MobileTableFilter = {};
  if (props.noFilters) return mobileFilter;
  props.columns
    .filter((column): column is ResponsiveTableColumn & Required<Pick<ResponsiveTableColumn, 'filter'>> => !!column.filter)
    .forEach(column => {
      switch (column.filter.type) {
        case 'FREE_SEARCH':
          if (!column.mobileBehaviour?.position) return;
          if (!mobileFilter.freeSearch) mobileFilter.freeSearch = { filterOn: [] };
          if (column.filter.initialValue) {
            mobileFilter.freeSearch.initialValue = column.filter.initialValue;
            mobileFilter.freeSearch.placeholder = column.filter.placeholder;
          }
          mobileFilter.freeSearch?.filterOn.push(
            ...(Array.isArray(column.mobileBehaviour.position)
              ? column.mobileBehaviour.position.map(position => position.to)
              : [column.mobileBehaviour.position]),
          );
          break;
        case 'DATE_RANGE':
          if (!mobileFilter.dateRange)
            mobileFilter.dateRange = {
              label: column.title,
              enabled: true,
              initialValue: column.filter.initialValue,
              placeholder: column.filter.placeholder,
            };
          break;
        case 'MULTI_VALUE':
          if (!column.mobileBehaviour?.position) return;
          if (!mobileFilter.multiValue) mobileFilter.multiValue = {};
          let position;
          if (Array.isArray(column.mobileBehaviour.position)) {
            position = column.filter.mobileBehaviour?.position ?? column.mobileBehaviour.position.at(0)?.to;
          } else {
            position = column.mobileBehaviour.position;
          }
          if (!position) return;
          mobileFilter.multiValue[position] = {
            enabled: true,
            initialValue: column.filter.initialValue,
            selectOptions: column.filter.selectOptions,
            label: column.title,
          };
          break;
        case 'RESET_FILTERS_BUTTON':
          mobileFilter.resetButton = true;
          break;
      }
    });
  return mobileFilter;
});

const mobileSelection = computed<ElMobileTableProps['selection']>(() => {
  if (props.mobileBehaviour?.selection === undefined) return undefined;
  return {
    enabled: props.rowsSelectionMode !== undefined && props.rowsSelectionDisabled !== true,
    selectionMode: props.rowsSelectionMode || 'multiple',
    destructiveAction: props.mobileBehaviour?.selection.destructiveAction,
    bulkActionButton: props.mobileBehaviour?.selection.bulkActionButton,
    selectedLabel: props.mobileBehaviour?.selection.selectedLabel,
    cancelText: props.mobileBehaviour?.selection.cancelText,
  } satisfies ElMobileTableProps['selection'];
});

const desktopRowsToResponsiveRows = (desktopRows: DataRow[]): ResponsiveDataRow[] => {
  const indexes = desktopRows.map(row => row.id).map(id => props.data.findIndex(row => row.id === id));
  return indexes.map(index => props.data[index]);
};

const mobileRowsToResponsiveRows = (mobileRows: MobileTableRow[]): ResponsiveDataRow[] => {
  const indexes = mobileRows.map(row => row.id).map(id => props.data.findIndex(row => row.id === id));
  return indexes.map(index => props.data[index]);
};

const mobileRowToResponsiveRow = (mobileRow: MobileTableRow): ResponsiveDataRow => {
  const index = props.data.findIndex(row => row.id === mobileRow.id);
  return props.data[index];
};

const mobileSortBy = computed<ElMobileTableProps['sortBy'] | undefined>(() => {
  if (!props.sortBy || !props.sortBy.column) return undefined;
  const column = props.columns[props.sortBy.column];
  if (!column.mobileBehaviour?.position || Array.isArray(column.mobileBehaviour.position)) return undefined;

  return {
    on: column.mobileBehaviour?.position,
    order: props.sortBy.order ?? 'desc',
  } satisfies ElMobileTableProps['sortBy'];
});
</script>

<template>
  <ElTable
    v-if="!isMobile"
    ref="desktopTable"
    :data="desktopItems"
    :columns="props.columns"
    :table-id="props.tableId"
    :sort-by-col="props.sortBy?.column"
    :sort-by-col-asc="props.sortBy?.order === 'asc'"
    :no-filters="props.noFilters"
    :no-footer="props.desktopBehaviour?.noFooter"
    :rows-selection-mode="props.rowsSelectionMode"
    :rows-selection-disabled="props.rowsSelectionDisabled"
    :initial-rows="props.desktopBehaviour?.initialRows"
    :loading="props?.loading"
    @rows-selected="data => $emit('rows-selected', desktopRowsToResponsiveRows(data))"
  />
  <ElTableMobile
    v-else
    ref="mobileTable"
    :data="mobileItems"
    :sort-by="mobileSortBy"
    :color="props.color"
    :filters="mobileFilters"
    :selection="mobileSelection"
    :loading="props?.loading"
    :prevent-default-item-click="props.mobileBehaviour?.preventDefaultItemClick"
    @rows-selected="data => $emit('rows-selected', mobileRowsToResponsiveRows(data))"
    @selection:exit="() => $emit('mobile:selection:exit')"
    @bulk:click="data => $emit('mobile:bulk:click', mobileRowsToResponsiveRows(data))"
    @destructive:click="data => $emit('mobile:destructive:click', mobileRowsToResponsiveRows(data))"
    @item:click="row => $emit('mobile:item:click', mobileRowToResponsiveRow(row))"
  >
    <slot name="mobile" />
  </ElTableMobile>
</template>
